import { CancelOutlined, Replay } from '@mui/icons-material'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
} from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  onReject: (reason: string) => void
  onUnreject: (reason: string) => void
  rejectMode: boolean
  onClose?: () => void
}

export const RejectMenuItem = ({ onReject, onUnreject, rejectMode, onClose }: Props) => {
  const { t } = useTranslation('requests')
  const [isDialogOpen, setDialogOpen] = useState(false)
  const [reason, setReason] = useState('')

  const handleConfirmDialog = () => {
    if (rejectMode) {
      onReject(reason)
    } else {
      onUnreject(reason)
    }
    handleCloseDialog()
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setReason('')
  }

  const handleButtonClick = () => {
    setDialogOpen(true)
    onClose?.()
  }

  return (
    <>
      {rejectMode ? (
        <MenuItem onClick={handleButtonClick}>
          <ListItemIcon>
            <CancelOutlined />
          </ListItemIcon>
          <ListItemText>{t('manage-request.reject')}</ListItemText>
        </MenuItem>
      ) : (
        <MenuItem onClick={handleButtonClick}>
          <ListItemIcon>
            <Replay />
          </ListItemIcon>
          <ListItemText>{t('manage-request.reopen')}</ListItemText>
        </MenuItem>
      )}
      <RejectionReasonDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDialog}
        reason={reason}
        onReasonChange={setReason}
        rejectMode={rejectMode}
      />
    </>
  )
}

interface RejectionReasonDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  reason: string
  onReasonChange: (reason: string) => void
  rejectMode: boolean
}

const RejectionReasonDialog = ({ open, onClose, onConfirm, reason, onReasonChange, rejectMode }: RejectionReasonDialogProps) => {
  const { t } = useTranslation('requests')

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle>{rejectMode ? t('rejection.reject-request') : t('rejection.reopen-request')}</DialogTitle>
      <DialogContent sx={{ gap: 4 }}>
        <DialogContentText>{rejectMode ? t('rejection.dialog-body') : t('rejection.dialog-reopen-body')}</DialogContentText>
        <TextField
          label={rejectMode ? t('rejection.dialog-label') : t('rejection.dialog-reopen-label')}
          multiline
          rows={5}
          sx={{ width: '100%' }}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
        />
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="neutral" onClick={onClose}>
          {t('rejection.cancel')}
        </Button>
        {rejectMode ? (
          <Button variant="contained" color="error" onClick={onConfirm} disabled={reason === ''}>
            {t('rejection.reject')}
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={onConfirm} disabled={reason === ''}>
            {t('rejection.confirm')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
